import { createApp } from "vue";
import { createPinia } from "pinia";
import App from "./App.vue";
import "./shared/styles/main.css";
import "remixicon/fonts/remixicon.css";
// Import Flowbite
import "flowbite";
// Import OPAL Design System styles (after Flowbite so OPAL takes precedence)
import "@eudonet/opal/styles";
import "@eudonet/opal/styles/design-tokens.css";
// Import Keycloak plugin
import keycloakPlugin from "./plugins/keycloak";
// Import router
import router from "./router";
// Import i18n plugin
import i18n from "./plugins/i18n";
import { logger } from "./infrastructure/services/LoggerService";
// Import ConfigService
import { configService } from "./infrastructure/services/ConfigService";
// Import language store
import { useLanguageStore } from "./stores/languageStore";
// Import Toast plugin
import { installToast } from "./shared/composables/useToast";
// Import LocaleSyncService
import { localeSyncService } from "./infrastructure/services/LocaleSyncService";
// Import PostHog BO service (FO is handled by the payment entry point)
import { postHogBOService } from "./infrastructure/services/PostHogBOService";
// Import Keycloak auth service directly (works outside Vue component context)
import { keycloakAuthService } from "./infrastructure/adapters/KeycloakAuthService";
// Import router registry for error handler decoupling
import { setRouter } from "./router/routerInstance";
// Import PostHog registry for error handler decoupling
import { setPostHogService } from "./infrastructure/services/postHogInstance";
async function initializeApp() {
    // Initialize configuration service first
    await configService.initialize();
    // Initialize PostHog Back Office if config available
    if (configService.posthogBO) {
        try {
            postHogBOService.initialize(configService.posthogBO);
            setPostHogService(postHogBOService);
        }
        catch (error) {
            logger.debug("Failed to initialize PostHog Back Office:", error);
        }
    }
    const app = createApp(App);
    // Create and use Pinia store
    const pinia = createPinia();
    app.use(pinia);
    // Initialize language store after Pinia is set up
    // This reads from localStorage or navigator.language (single source of truth)
    // Priority: localStorage > browser > default
    // Later overridden by: Keycloak (highest) > API (medium)
    const languageStore = useLanguageStore();
    languageStore.initializeFromStorage();
    // Install i18n plugin
    app.use(i18n);
    // Sync i18n with Language Store's detected locale
    // i18n starts with DEFAULT_LANGUAGE_TAG, we update it with the detected locale
    // @ts-expect-error - i18n locale type is more restrictive, but we know our locale is valid
    i18n.global.locale.value = languageStore.currentLocale;
    document.documentElement.lang = languageStore.currentLocale;
    logger.debug(`i18n synchronized with Language Store: ${languageStore.currentLocale} (source: ${languageStore.source})`);
    // Install router
    app.use(router);
    // Register router with the error handler
    setRouter(router);
    // Install Keycloak plugin
    app.use(keycloakPlugin);
    // Install Toast notification system
    installToast(app);
    // Initialize LocaleSyncService to listen for locale changes and sync with Keycloak
    localeSyncService.initialize();
    // Initialize PostHog user identification after Keycloak is ready
    // Using keycloakAuthService directly (works outside Vue component context)
    {
        const identifyUserInPostHog = () => {
            if (!postHogBOService.isInitialized() || !keycloakAuthService.isAuthenticated()) {
                return;
            }
            const userInfo = keycloakAuthService.getUserInfo();
            const userId = userInfo?.sub || userInfo?.email;
            const roles = keycloakAuthService.getClientRoles("one-platform-payment");
            if (userId) {
                postHogBOService.identifyUser(userId, roles);
            }
        };
        // Wait for both services to be ready, then identify user
        const waitAndIdentify = () => {
            if (keycloakAuthService.isInitialized() && postHogBOService.isInitialized()) {
                identifyUserInPostHog();
            }
            else {
                setTimeout(waitAndIdentify, 200);
            }
        };
        setTimeout(waitAndIdentify, 500);
    }
    app.mount("#app");
    document.title = i18n.global.t("title");
}
// Initialize the app
initializeApp();
// Handle URL cleanup and navigation for logout page with Keycloak parameters
const handleKeycloakUrlCleanup = () => {
    const hash = window.location.hash;
    const pathname = window.location.pathname;
    // Clean up hash params on error page
    if (pathname === "/error") {
        logger.debug(`On error page, cleaning up URL`);
        if (hash) {
            window.history.replaceState({}, document.title, pathname);
        }
        return;
    }
    // For other pages, handle Keycloak errors
    if (hash.includes("error=") && !hash.includes("error=login_required")) {
        logger.debug("Keycloak error (non-login_required) detected in URL, cleaning up parameters");
        // Clean up URL parameters for non-login_required errors
        // login_required errors are handled by the router's navigation guard
        window.history.replaceState({}, document.title, pathname);
    }
};
// Run cleanup immediately and again after app mounts
handleKeycloakUrlCleanup();
setTimeout(handleKeycloakUrlCleanup, 300);
